import { useEffect, useRef, useState } from 'react';
import {
  VideoCallManager,
  VideoCallState,
  MediaProvider,
} from './VideoCallManager';
import { createSocketSignaling } from './socketSignaling';

/** Feature flag — video is dark-launched (RFC-0001 §7). Off unless explicitly enabled. */
export function isVideoEnabled(): boolean {
  return process.env.REACT_APP_VIDEO_ENABLED === 'true';
}

const browserMedia: MediaProvider = {
  getLocalStream: () =>
    navigator.mediaDevices.getUserMedia({ video: true, audio: true }),
};

export interface UseVideoCallOptions {
  /** Master switch — when false the hook is inert (no media, no manager). */
  enabled: boolean;
  /** True for the therapist (host), who sends the offer once the peer is present. */
  isInitiator: boolean;
  /** True once the other participant has joined the room. */
  peerPresent: boolean;
  /**
   * Runtime on/off for THIS session. The therapist decides whether video is
   * part of the session; when false the manager is torn down (call ends, camera
   * released). Defaults to true so callees (patient) always listen — their
   * camera still only turns on once the therapist actually starts the call.
   */
  active?: boolean;
}

export interface UseVideoCallResult {
  state: VideoCallState;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  hangUp: () => void;
}

/**
 * Manages a single 1:1 VideoCallManager for a therapy session.
 *
 * - On mount (when enabled) it acquires local media and listens for an offer.
 * - The initiator (therapist) sends the offer once the peer is present.
 * - Everything is additive: if media is denied the state becomes 'declined'
 *   and the BLS session continues unaffected (RFC-0001 §6).
 */
export function useVideoCall({
  enabled,
  isInitiator,
  peerPresent,
  active = true,
}: UseVideoCallOptions): UseVideoCallResult {
  const [state, setState] = useState<VideoCallState>('idle');
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const managerRef = useRef<VideoCallManager | null>(null);
  const offeredRef = useRef(false);

  // The manager exists only while the feature is enabled AND the session has
  // video switched on. Turning `active` off tears the call down (camera freed).
  const live = enabled && active;

  // Create/destroy the manager with the live flag.
  useEffect(() => {
    if (!live) return;
    const manager = new VideoCallManager({
      signaling: createSocketSignaling(),
      media: browserMedia,
      onStateChange: setState,
      onLocalStream: setLocalStream,
      onRemoteStream: setRemoteStream,
    });
    managerRef.current = manager;
    // Be ready to answer inbound offers (media acquired lazily on offer).
    void manager.acceptCalls();

    return () => {
      offeredRef.current = false;
      manager.hangUp();
      managerRef.current = null;
      setState('idle');
      setLocalStream(null);
      setRemoteStream(null);
    };
  }, [live]);

  // The therapist initiates the offer once the client is present (once).
  useEffect(() => {
    if (!live || !isInitiator || !peerPresent) return;
    const manager = managerRef.current;
    if (!manager || offeredRef.current) return;
    offeredRef.current = true;
    void manager.startCall();
  }, [live, isInitiator, peerPresent]);

  // When the peer leaves (toggled off, navigated away, or CLOSED their tab/
  // browser — surfaced by the gateway's presence events), drop the now-dead
  // remote video instead of showing a frozen frame, and get ready to reconnect
  // if they come back.
  useEffect(() => {
    if (!live || peerPresent) return;
    const manager = managerRef.current;
    if (!manager) return;
    manager.resetCall();
    offeredRef.current = false;
    setRemoteStream(null);
    setLocalStream(null);
  }, [live, peerPresent]);

  return {
    state,
    localStream,
    remoteStream,
    hangUp: () => managerRef.current?.hangUp(),
  };
}
